import { useCallback, useMemo } from 'react';
import {
  addDays,
  addMonths,
  civilToday,
  compareCivilDates,
  isCivilDate,
  useListSearch,
  type CivilDate,
} from '@eis/core';

/**
 * What a list is about in time (SDD-021 PC-20, §9): ONE business day — *Stichtag*, the members
 * active that day — or a PERIOD — *Zeitraum*, everything covered at any time in it.
 *
 * A period is half-open, as the API takes it: `from` inclusive, `to` EXCLUSIVE (`coveredTo`,
 * `periodTo`). People read periods inclusively ("1. bis 30. November"), so the selector shows and
 * edits the LAST day and converts at the edge — the one place the two conventions meet.
 */
export type ValidityPeriod =
  | {
      readonly kind: 'day';
      /** Absent = today, the service's default. */ readonly validAt?: CivilDate;
    }
  | { readonly kind: 'period'; readonly from: CivilDate; /** Exclusive. */ readonly to: CivilDate };

/** The URL keys a validity period lives in (rule 8d: list state is the URL's). */
export interface ValidityPeriodSearch {
  readonly validAt?: string;
  readonly coveredFrom?: string;
  readonly coveredTo?: string;
}

/** The period in the URL: a complete, non-empty `coveredFrom`/`coveredTo`, else the day. */
export function validityPeriodOf(search: ValidityPeriodSearch): ValidityPeriod {
  const { coveredFrom, coveredTo, validAt } = search;
  if (
    isCivilDate(coveredFrom) &&
    isCivilDate(coveredTo) &&
    compareCivilDates(coveredFrom, coveredTo) < 0
  ) {
    return { kind: 'period', from: coveredFrom, to: coveredTo };
  }
  return isCivilDate(validAt) ? { kind: 'day', validAt } : { kind: 'day' };
}

/**
 * The URL patch for a validity period: the keys of the other form are EMPTIED (`undefined`), so
 * `nextListSearch` drops them — a period and a day are never in the URL together (the service
 * refuses both with VALID_AT_AND_PERIOD_EXCLUSIVE).
 */
export function validityPeriodSearch(value: ValidityPeriod): {
  validAt: string | undefined;
  coveredFrom: string | undefined;
  coveredTo: string | undefined;
} {
  return value.kind === 'period'
    ? { validAt: undefined, coveredFrom: value.from, coveredTo: value.to }
    : { validAt: value.validAt, coveredFrom: undefined, coveredTo: undefined };
}

/** The calendar month a day falls in, half-open: `2026-11-15` → `2026-11-01` … `2026-12-01`. */
export function monthPeriodOf(day: CivilDate): {
  readonly from: CivilDate;
  readonly to: CivilDate;
} {
  const from = `${day.slice(0, 7)}-01`;
  return { from, to: addMonths(from, 1) };
}

/** The last day of a half-open period — what a reader is shown. */
export const lastDayOf = (to: CivilDate): CivilDate => addDays(to, -1);

/** The exclusive end of a period whose last day is `lastDay` — what the API is sent. */
export const endAfter = (lastDay: CivilDate): CivilDate => addDays(lastDay, 1);

/** A period whose last day is not before its first: one day at least. */
export const isOrderedPeriod = (from: CivilDate, lastDay: CivilDate): boolean =>
  compareCivilDates(from, lastDay) <= 0;

/** The day a period selector starts from when it switches to *Zeitraum*: the chosen day, or today. */
export const anchorDayOf = (value: ValidityPeriod): CivilDate =>
  value.kind === 'period' ? value.from : (value.validAt ?? civilToday());

/**
 * A list's validity period, read from and written to the URL — `validAt` for a day,
 * `coveredFrom`/`coveredTo` for a period — through the list URL state (`useListSearch`), so the
 * back button undoes a change and a link carries it.
 */
export function useValidityPeriodUrlState(): {
  readonly value: ValidityPeriod;
  readonly set: (next: ValidityPeriod) => void;
} {
  const { search, set } = useListSearch();
  const { validAt, coveredFrom, coveredTo } = search;
  const value = useMemo(
    () => validityPeriodOf({ validAt, coveredFrom, coveredTo }),
    [validAt, coveredFrom, coveredTo],
  );
  const update = useCallback(
    (next: ValidityPeriod) => {
      set(validityPeriodSearch(next));
    },
    [set],
  );
  return { value, set: update };
}
